/**
 * Jazz binding for the `OpenProject` port: loads a project CoMap and wraps
 * it in `JazzOpenProject`. Components go through `hooks/useOpenProject`
 * rather than importing this directly, so the backend choice lives in one
 * place.
 */
import { useMemo, useSyncExternalStore } from "react";
import { useAccount, useCoState } from "jazz-tools/react";
import { DiagramProject, VibeDiagramAccount } from "./schema";
import { JazzOpenProject } from "./JazzOpenProject";
import type { OpenProject, OpenProjectSnapshot } from "../stores/OpenProject";

interface UseOpenProjectResult {
  project: OpenProject | null;
  /** Null until the project has loaded. */
  snapshot: OpenProjectSnapshot | null;
  isLoading: boolean;
}

/** Stable no-op for the not-yet-loaded case, so hook order stays constant. */
const NO_SUBSCRIBE = () => () => {};

export function useJazzOpenProject(projectId?: string): UseOpenProjectResult {
  const { me } = useAccount(VibeDiagramAccount, {
    resolve: { root: { projects: true } },
  });

  // Resolve nested annotations + strokes + cursors so remote writers'
  // strokes and cursor ticks arrive; without this query the children load
  // lazily and updates never reach the overlay.
  //
  // `files` down to `updates` is resolved for a sharper reason: the editor
  // asks for a file's shared text once, when it opens the file, and binds
  // CodeMirror to whatever comes back. A project whose file list has not
  // loaded yet answers "no shared text" for every file, so the editor binds
  // to the whole-document write path and stays there — which is the path
  // that overwrites other writers. Waiting here costs a moment of the
  // loading state and makes the answer correct the first time.
  const document = useCoState(DiagramProject, projectId, {
    resolve: {
      annotations: { strokes: { $each: true }, cursors: true },
      // `$onError: null` on the update list: without it a single `TextUpdate`
      // that cannot be loaded — unauthorized, unavailable, not yet synced —
      // drops the whole `document` to null and the app renders "Diagram not
      // found" for a project that exists. On error the field reads as null,
      // which the adapter already treats as "not loaded yet" rather than "no
      // log", so it will not mint a second one.
      files: {
        $each: { content: true, updates: { $each: true, $onError: null } },
      },
    },
  });

  const project = useMemo(
    () => (document && me ? new JazzOpenProject(document, me) : null),
    [document, me],
  );

  const snapshot = useSyncExternalStore(
    useMemo(
      () => (project ? project.subscribe.bind(project) : NO_SUBSCRIBE),
      [project],
    ),
    () => project?.getSnapshot() ?? null,
  );

  // The account matters as much as the document: the component's own gate is
  // now the shallow `useCurrentUser`, which resolves well before this deeply
  // resolved account does. Without `!me` here, a document that arrives first
  // yields project === null with isLoading === false, and the caller renders
  // "Diagram not found" for a diagram that exists.
  return {
    project,
    snapshot,
    isLoading: (!document || !me) && !!projectId,
  };
}
